import { useMemo, useState } from "react";

import { DASHBOARD_TYPES, Dashboard, DashboardType, Workspace } from "models/workspace";

import { useWorkspaceDashboards } from "../../hooks/useWorkspaceDashboards";
import { getNewDashboardName } from "../../utils/dashboardName";
import { getCreatableDashboardTypes } from "../../utils/getCreatableDashboardTypes";
import { CloneFormProps } from "../CloneForm";

interface UseCloneFormParams {
  dashboard: Dashboard;
  workspace?: Workspace;
  cloneDashboard?: (title: string, type: string) => Promise<void>;
  isAtLeastPowerUser: boolean;
}

interface UseCloneFormReturn {
  /**
   * Everything CloneForm needs, or null when the user can create no dashboard type in
   * this workspace. Callers hide the clone affordance on null rather than opening it
   * onto an empty type list — bundling the two makes the pairing impossible to get
   * wrong, which a sibling boolean flag could not do under `strict: false`.
   */
  formProps: Nullable<CloneFormProps>;

  // Popover state
  isPopoverVisible: boolean;
  setIsPopoverVisible: (visible: boolean) => void;

  // Tooltip control - prevents tooltip from showing immediately after popover closes
  canShowTooltip: boolean;

  // Actions
  handleOpenPopover: () => void;
  handleMouseEnter: () => void;
}

export function useCloneForm({
  dashboard,
  workspace,
  cloneDashboard,
  isAtLeastPowerUser
}: UseCloneFormParams): UseCloneFormReturn {
  const workspaceDashboards = useWorkspaceDashboards(workspace);
  const [title, setTitle] = useState("");
  const [selectedType, setSelectedType] = useState<DashboardType>(
    DASHBOARD_TYPES.Personal
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [canShowTooltip, setCanShowTooltip] = useState(true);

  const typeOptions = useMemo(
    () => getCreatableDashboardTypes(workspace, isAtLeastPowerUser),
    [workspace, isAtLeastPowerUser]
  );

  // The selection is derived rather than stored so it can never be left pointing at a
  // type this workspace cannot host — the workspace resolves after first render, and it
  // can change under an open popover.
  const type = typeOptions.includes(selectedType) ? selectedType : typeOptions[0];

  const handleClone = async () => {
    if (title.trim() && type && cloneDashboard) {
      try {
        setIsLoading(true);
        setError(""); // Clear any previous errors

        await cloneDashboard(title.trim(), type);

        // Only close popover and reset on success
        setIsPopoverVisible(false);
        setTitle("");
        setError("");
      } catch (err) {
        // Set error and keep popover open
        setError(err instanceof Error ? err.message : "Failed to clone dashboard");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setIsPopoverVisible(false);
    setError(""); // Clear error when canceling
  };

  const handleOpenPopover = () => {
    const newName = getNewDashboardName(workspaceDashboards.length, dashboard.title);
    setTitle(newName);
    setError(""); // Clear any previous errors when opening
  };

  const handleSetIsPopoverVisible = (isVisible: boolean) => {
    setIsPopoverVisible(isVisible);
    setCanShowTooltip(false);
  };

  const handleMouseEnter = () => {
    if (!isPopoverVisible) {
      setCanShowTooltip(true);
    }
  };

  return {
    formProps: type
      ? {
          title,
          setTitle,
          type,
          setType: setSelectedType,
          typeOptions,
          isLoading,
          error,
          onClone: handleClone,
          onCancel: handleCancel
        }
      : null,

    // Popover state
    isPopoverVisible,
    setIsPopoverVisible: handleSetIsPopoverVisible,
    canShowTooltip,

    // Actions
    handleOpenPopover,
    handleMouseEnter
  };
}
